import { useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, X } from "lucide-react";
import api from "@/lib/api";
import { fmtMm, fmtM2 } from "@/components/drawing/Ruler";

// V-Check-Zielfelder: Einheit bestimmt die Umrechnung aus mm bzw. m².
const LENGTH_FIELDS = [
  ["route_length_m", "Gesamter Leitungsweg", "m"],
  ["route_height_diff_m", "Höhenunterschied Leitungsweg", "m"],
  ["distance_window_m", "Abstand zu Fenster/Tür", "m"],
  ["distance_border_m", "Abstand zur Grundstücksgrenze", "m"],
  ["distance_bedroom_m", "Abstand zum Schlafraum (Nachbar)", "m"],
  ["cable_distance_m", "Entfernung Zählerschrank → WP", "m"],
  ["cable_route_length_m", "Kabelweg gesamt", "m"],
  ["indoor_space_width_cm", "Freier Stellraum – Breite", "cm"],
  ["indoor_space_depth_cm", "Freier Stellraum – Tiefe", "cm"],
  ["indoor_space_height_cm", "Freier Stellraum – Höhe", "cm"],
  ["narrowest_transport_cm", "Engste Tür-/Transportbreite", "cm"],
  ["narrow_width_cm", "Engster Durchgang – Breite", "cm"],
  ["narrow_height_cm", "Engster Durchgang – Höhe", "cm"],
  ["door1_width_cm", "Tür 1 – Breite", "cm"],
  ["door1_height_cm", "Tür 1 – Höhe", "cm"],
  ["door2_width_cm", "Tür 2 – Breite", "cm"],
  ["door2_height_cm", "Tür 2 – Höhe", "cm"],
  ["core_drill_diameter_mm", "Ø Kernbohrung", "mm"],
];
const AREA_FIELDS = [
  ["heated_area", "Beheizte Fläche", "m²"],
  ["solar_collector_area", "Kollektorfläche Solarthermie", "m²"],
];

const convert = (kind, value, unit) => {
  if (kind === "area") return Math.round(value * 100) / 100;
  if (unit === "m") return Math.round(value / 10) / 100;
  if (unit === "cm") return Math.round(value / 10);
  return Math.round(value);
};

// Leiste nach jeder Messung: Wert optional in ein V-Check-Feld übernehmen.
export function VCheckTransferBar({ ruler, projectId }) {
  const t = ruler.transfer;
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  if (!t) return null;
  const fields = t.kind === "area" ? AREA_FIELDS : LENGTH_FIELDS;
  const field = fields.find((f) => f[0] === key);
  const val = field ? convert(t.kind, t.value, field[2]) : null;
  const close = () => { setKey(""); ruler.setTransfer(null); };

  const apply = async () => {
    setBusy(true);
    try {
      const cur = (await api.get(`/projects/${projectId}/vcheck-wp`)).data?.data?.[key];
      if (cur !== undefined && cur !== null && cur !== "" &&
          !window.confirm(`„${field[1]}“ enthält bereits ${cur} ${field[2]}. Mit ${val.toLocaleString("de-DE")} ${field[2]} überschreiben?`)) {
        setBusy(false);
        return;
      }
      await api.patch(`/projects/${projectId}/vcheck-wp`, { data: { [key]: val } });
      toast.success(`V-Check: ${field[1]} = ${val.toLocaleString("de-DE")} ${field[2]}`);
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernahme in den V-Check fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-x-3 bottom-24 sm:left-1/2 sm:-translate-x-1/2 sm:w-[460px] z-[85] rounded-2xl border border-sky-500/40 bg-[#0F1014]/95 backdrop-blur-xl p-3 shadow-2xl"
         data-testid="vcheck-transfer-bar">
      <div className="flex items-center gap-2 mb-2">
        <ClipboardCheck size={15} className="text-sky-300" />
        <div className="text-[13px] font-semibold text-zinc-100 flex-1">
          {t.kind === "area" ? fmtM2(t.value) : fmtMm(t.value)} in den V-Check übernehmen?
        </div>
        <button type="button" onClick={close} data-testid="vcheck-transfer-close" title="Nicht übernehmen"
                className="h-7 w-7 rounded-lg text-zinc-500 hover:text-zinc-200 flex items-center justify-center"><X size={15} /></button>
      </div>
      <div className="flex gap-2">
        <select value={key} onChange={(e) => setKey(e.target.value)} data-testid="vcheck-transfer-field"
                className="flex-1 min-w-0 h-10 rounded-xl bg-zinc-900 border border-zinc-700 px-2 text-[13px] text-zinc-100">
          <option value="">Feld wählen …</option>
          {fields.map(([k, label, unit]) => (
            <option key={k} value={k}>{label} → {convert(t.kind, t.value, unit).toLocaleString("de-DE")} {unit}</option>
          ))}
        </select>
        <button type="button" disabled={!key || busy} onClick={apply} data-testid="vcheck-transfer-apply"
                className="h-10 px-4 rounded-xl bg-sky-500 text-white text-[13px] font-bold disabled:opacity-40">Übernehmen</button>
      </div>
    </div>
  );
}
